/*
 * @Author: Merlin
 * @Date: 2024-07-30 17:37:36
 * @LastEditors: Merlin
 * @LastEditTime: 2024-10-25 16:40:37
 * @Description: 
 */

import {aTestData, aTestArrData02} from '@/pages/casino/futuresGame/ts/wave-chart-about';
import { useUserStore } from '~/stores/user'; 
import {
    oTable4Types,
    oPairsMap,
} from './dto/waves.dto'; 
import type {
    WavesSettings,
    Par4FuturesTick,
    Par4WavesLeave,
    ConfirmSetting,
    // ↓ 建仓、平仓、历史仓、接口参数 ---------------- 
    WavesPositionCreate,
    WavesPositionUpdate,
    WavesPositionCloseOne,
    WavesPositionHistory,
    // ↑ 请求参数 --------------------------- 
    // ↓ 接口返回内容 ---------------------------  
    WavesHistoryData, 
    ResTickData,
} from './dto/waves.dto'; 

const isDev = 1 || import.meta.dev;


// ↓ 收到一条
// 42["tick",{"data":{"tick":{"time":1722330000,"open":1245.5941126007706,"high":1626.329735282864,"low":1234.0213081224038,"close":1626.1099618208113},"pairs":{"XRP_USDT":0.60045769,"BTC_USDT":66706.84266743,"ETH_USDT":3334.78234771,"BNB_USDT":572.94693862,"FuryWaves_USDT":1626.1099618208113,"SOL_USDT":181.61627352,"DOGE_USDT":0.12923836,"BCH_USDT":436.57733651,"AVAX_USDT":27.03323973,"ADA_USDT":0.40087061,"NOT_USDT":0.01312256},"stat24":{"high":1626.329735282864,"low":772.0316340316961,"wager":2328893.8499999996}}}]	

// ↓ 查询一片
// 4232["futures.ticks",{"pair":"FuryWaves_USDT","rangeMs":15000,"date":1722334200}]	


export const useWavesSocket = (rootPath = 'futures') => {
    // ↓ 查询配置信息 
    // ↓ 第 1️⃣ 步最先执行的方法，就是此方法 
    const EMIT_GET_WAVES_SETTINGS = async ()=>{
        // 原版： 425["futures.settings",{}] 
        const res = await asyncEmit<
            WavesSettings
        >(
            `${rootPath}.settings`, 
            {},
        );
        return res;
    };

    // ↓ 查询某“时段” 的数据 💥 
    // ● 查询会返回一定时间范围的数据，
    // ● 也会变更实时数据的推送内容
    // ● 也会记录当前的 rangeMs 值到服务器 
    const EMIT_WAVES_FUTURES_TICK = async (
        oParams: Par4FuturesTick, 
    )=>{
        const res = await asyncEmit<ResTickData>(
            `${rootPath}.ticks`, 
            oParams, 
        );
        if (res?.data?.length && import.meta.dev){
            // ↓ 偶有跳帧发生
            const rangeMs = oParams.rangeMs / 1_000;
            res.data.forEach((cur, idx, arr) => {
                if (idx === 0) return; 
                const {time: preTime} = arr[idx - 1]; 
                const thisGap = cur.time - preTime; 
                if (thisGap == rangeMs) return; 
                console.error(`跳帧： ${rangeMs} => ${thisGap}`);
            });
        }
        // 4260["futures.ticks",{"pair":"BTC_USDT","rangeMs":3600000,"date":1725739200}] 
        return res;
    };


    // ↓ 终止实时数据推送 
    const EMIT_WAVES_LEAVE = async (
        oParams: Par4WavesLeave,
    )=>{
        const res = await asyncEmit(
            `${rootPath}.leave`, 
            oParams,
        );
        return res;
    };

    // ↓ 修改平仓确认提示的设定  
    // ↓ 保存 settings 设定（主要是平仓操作的提示规则） 
    const EMIT_WAVES_UPDATE_SETTINGS = async (
        oParams: ConfirmSetting, 
    )=>{
        const res = await asyncEmit(
            `${rootPath}.updateSettings`, 
            oParams,
        );
        return res;
    };

    // ===========================================================
    // ======== ↓ 建仓、平仓、已建仓修改，查询 ====================== 
    // ===========================================================

    // ↓ 建仓
    const EMIT_WAVES_POSITION_CREATE = async (
        oParams: WavesPositionCreate, 
    )=>{
        const res = await asyncEmit<any>( 
            `${rootPath}.createPosition`, 
            oParams,
        );
        return res;
    };
    // ↓ 建仓后修改
    const EMIT_WAVES_POSITION_UPDATE = async (
        oParams: WavesPositionUpdate, 
    )=>{
        const res = await asyncEmit(
            `${rootPath}.updatePosition`, 
            oParams,
        );
        return res;
    };

    // ↓ 平仓“一项”
    const EMIT_WAVES_POSITION_CLOSE_ONE = async ( 
        oParams: WavesPositionCloseOne, 
    )=>{
        const res = await asyncEmit(
            `${rootPath}.closePosition`, 
            oParams,
        );
        return res;
    };
    
    // ↓ 平仓“全部”  
    const EMIT_WAVES_POSITION_CLOSE_ALL = async ()=>{
        const res = await asyncEmit(
            `${rootPath}.closeAllPositions`, 
            {},
        );
        return res;
    };

    // ↓ 查询 positions，取得游戏下方大表格的数据 
    const EMIT_WAVES_POSITION_HISTORY = async (
        oParams: WavesPositionHistory, 
    )=>{
        const res = await asyncEmit<
            WavesHistoryData
        >(
            `${rootPath}.positions`, 
            oParams,
        );
        const isLeaderboard = oParams.type === oTable4Types.leaderboard; 
        const arr = res?.data?.items;
        const {hasLogged} = await getUserInfo();
        if (isLeaderboard && arr && hasLogged){
            const userStore = useUserStore();
            for (const cur of arr){
                if (cur.userId == userStore.id){
                    cur.bold = true;
                    break; // 只标记一次！！！！
                }
            }
        }
        return res;
    };

    // ===== ↓ 监听目标数据 ==================================== 

    // ↓ 接收图表实时波动数据 
    const ON_WAVES_TICK = (
        callback: (...args: any[]) => void, 
    )=>{
        const api = `tick`;
        socket.on(api, callback);
        return () => {
            socket.off(api, callback); 
        };
    };
    // ↓ 接收当前用户合约的最新状态：修改中，已平仓等
    const ON_WAVES_CONTRACT_UPDATED = (
        callback: (...args: any[]) => void, 
    )=>{
        const api = `futuresUpdate`;
        // "state": "cashOutProcessing", "cashed", 
        socket.on(api, callback);
        return () => {
            socket.off(api, callback); 
        };
    };
    // ↓ 接收 public 公共的平仓数据 
    const ON_WAVES_PUBLIC_UPDATED = (
        callback: (...args: any[]) => void,  
    )=>{
        // 经查，不在 public 页时也在接收此数据， 
        const api = `publicPosition`;
        socket.on(api, callback);
        return () => {
            socket.off(api, callback); 
        };
    };

    // ↓ 接收倒计时时间点更新的数据  
    const ON_WAVES_SETTINGS_UPDATE = (
        callback: (...args: any[]) => void,
    )=>{
        if (0 && import.meta.dev){
            setInterval(()=>{
                const aPairs = Object.values(oPairsMap);
                const sKey = aPairs[Math.floor(Math.random() * aPairs.length)]; 
                const hours = Math.floor(Math.random() * 5 + 1) * (1000*60*60); 
                const iTime = Date.now() + hours; 
                const fakeData = {
                    "data": {"pairs": {[sKey]: {"endOfFundingPeriod": iTime}}}, 
                };
                return callback(fakeData);
            }, 1_000 * 10);
        }
        // 经查，不在 public 页时也在接收此数据， 
        const api = `futuresSettingsUpdate`;
        socket.on(api, callback);
        // ↓ 接收数据示例：
        // 42["futuresSettingsUpdate",{"data":{"pairs":{"AVAX_USDT":{"endOfFundingPeriod":1729497600000}}}}]	
        return () => {
            socket.off(api, callback); 
        };
    };
    
    // ===== 最终导出 ==================================== 
    return {
        EMIT_GET_WAVES_SETTINGS, // 01 查询设定 
        EMIT_WAVES_FUTURES_TICK, // 查询某“时段” 的数据， 
        EMIT_WAVES_UPDATE_SETTINGS, // 修改平仓确认提示的设定 
        EMIT_WAVES_LEAVE, // 退出 Waves 游戏 
        // --- ↓ 用户建仓平仓相关 --------------------------------- 
        EMIT_WAVES_POSITION_CREATE, // 建仓 01 
        EMIT_WAVES_POSITION_UPDATE, // 建仓后修改 
        EMIT_WAVES_POSITION_HISTORY, // 建仓后查询（取得游戏下方大表格的数据）  
        EMIT_WAVES_POSITION_CLOSE_ONE, // 平仓“一项”  
        EMIT_WAVES_POSITION_CLOSE_ALL, // 平仓“全部”  
        // --- ↓ 被动接收的数据 --------------------------------- 
        ON_WAVES_TICK, // 接收实时波动值
        ON_WAVES_CONTRACT_UPDATED, 
        ON_WAVES_PUBLIC_UPDATED, 
        ON_WAVES_SETTINGS_UPDATE, 
    };
};












